import { useState, useEffect, useRef } from 'react';
import './App.css';
import logo from '/cropped_logo.png';
import TelegramModal from './components/TelegramModal';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const sectionRefs = useRef<{[key: string]: HTMLElement | null}>({});
  const timelineRef = useRef<HTMLDivElement>(null);
  const animationStarted = useRef(false);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Animate timeline items one by one
  useEffect(() => {
    if (visibleSections.has('timeline') && !animationStarted.current) {
      animationStarted.current = true;
      const timelineElement = timelineRef.current;
      const timelineItems = document.querySelectorAll('.timeline-item');
      const delay = 500; // ms between items
      
      // Start the connecting line animation
      if (timelineElement) {
        timelineElement.classList.add('animate-line');
      }
      
      // Animate items one by one
      timelineItems.forEach((_, index) => {
        setTimeout(() => {
          setVisibleItems(prev => [...prev, index]);
        }, index * delay);
      });
    }
  }, [visibleSections]);

  // Handle navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Set up Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const sectionId = entry.target.getAttribute('id');
          if (sectionId) {
            setVisibleSections(prev => {
              const newSet = new Set(prev);
              if (entry.isIntersecting) {
                newSet.add(sectionId);
              } else {
                newSet.delete(sectionId);
              }
              return newSet;
            });
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all section elements
    const sections = Object.values(sectionRefs.current).filter(Boolean);
    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    // Initial check for elements in viewport
    sections.forEach(section => {
      if (section) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          const sectionId = section.getAttribute('id');
          if (sectionId) {
            setVisibleSections(prev => new Set(prev).add(sectionId));
          }
        }
      }
    });

    return () => observer.disconnect();
  }, []);

  // Helper function to set section refs
  const setSectionRef = (sectionId: string) => (el: HTMLElement | null) => {
    if (el) {
      sectionRefs.current[sectionId] = el;
    }
  };

  return (
    <div className="app">
      <TelegramModal
        isOpen={showTelegramModal}
        onClose={() => setShowTelegramModal(false)}
        onConfirm={() => {
          setShowTelegramModal(false);
          window.open('https://t.me/constflux', '_blank');
        }}
      />
      {/* Top Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="navbar-logo">
            <img src={logo} alt="Constflux" className="nav-logo" />
            <span className="brand-name">ConstFlux</span>
          </div>
          
          {/* Desktop Navigation - Always visible on desktop */}
          <div className="desktop-nav">
            <div className="nav-buttons">
              <button className="nav-link" onClick={() => scrollToSection('constflux-view')}>View</button>
              <button className="nav-link" onClick={() => scrollToSection('ai-agents')}>Agents</button>
              <button className="nav-link" onClick={() => scrollToSection('timeline')}>Timeline</button>
            </div>
            <button 
              className="nav-button"
              onClick={() => setShowTelegramModal(true)}
            >
              Get Started
            </button>
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="hamburger-menu" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
          
          {/* Mobile Navigation - Only visible when menu is open */}
          {isMenuOpen && (
            <div className="mobile-nav">
              <div className="nav-buttons">
                <button 
                  className="nav-link" 
                  onClick={() => {
                    scrollToSection('constflux-view');
                    setIsMenuOpen(false);
                  }}
                >
                  View
                </button>
                <button 
                  className="nav-link" 
                  onClick={() => {
                    scrollToSection('ai-agents');
                    setIsMenuOpen(false);
                  }}
                >
                  Agents
                </button>
                <button 
                  className="nav-link" 
                  onClick={() => {
                    scrollToSection('timeline');
                    setIsMenuOpen(false);
                  }}
                >
                  Timeline
                </button>
              </div>
              <button 
                className="nav-button"
                onClick={() => {
                  setShowTelegramModal(true);
                  setIsMenuOpen(false);
                }}
              >
                Get Started
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        id="hero" 
        ref={setSectionRef('hero')} 
        className={`hero ${visibleSections.has('hero') ? 'animate-in' : ''}`}
      >
        <div className="hero-dots hero-dots-left"></div>
        <div className="hero-dots hero-dots-right"></div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              You were the signal<br />
              <span className="gradient-text">Now you're the noise</span>
            </h1>
            <p className="hero-description">
              Constflux is building a self-sustaining network of AI agents that control crypto assets and evolve new strategies without human input. We deliver unmatched market analysis powered by these agents—faster, smarter, and more accurate than anything offered by traditional firms.
            </p>
            <div className="hero-actions">
              <button 
                className="cta-button"
                onClick={() => setShowTelegramModal(true)}
              >
                Get Started
              </button>
              <div className="hero-image">
                <img src="/Constflux-View.png" alt="Constflux View Visualization" style={{ width: '100%', borderRadius: '12px' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Agents Section */}
      <section 
        id="ai-agents" 
        ref={setSectionRef('ai-agents')} 
        className={`ai-agents ${visibleSections.has('ai-agents') ? 'animate-in' : ''}`}
      >
        <div className="container">
          <div className="section-image">
            <img src="/ConstFlux-Agents.png" alt="Constflux AI Agents Visualization" style={{ width: '100%', borderRadius: '12px' }} />
          </div>
          <div className="section-content">
            <h2>Constflux AI Agents</h2>
            <h3>Relentless. Precise. Unstoppable.</h3>
            <p>Our AI agents don't just watch the market — they dominate it.</p>
            <p>They analyze every tick, exploit every opportunity, and execute with lightning speed.</p>
            <p>Powered by cutting-edge algorithms, built for traders who demand the edge.</p>
            <button className="secondary-button">Learn More</button>
          </div>
        </div>
      </section>

      {/* Constflux View Section */}
      <section 
        id="constflux-view" 
        ref={setSectionRef('constflux-view')} 
        className={`ai-agents ${visibleSections.has('constflux-view') ? 'animate-in' : ''}`}
      >
        <div className="container">
          <div className="section-content">
            <h2>Constflux View</h2>
            <h3>The edge isn't visible. Until now.</h3>
            <p>We decode market noise into actionable clarity.</p>
            <p>Live data. Proprietary signals. Zero distractions.</p>
            <p>For traders who treat seconds like assets.</p>
            <button className="secondary-button">Learn More</button>
          </div>
          <div className="section-image">
            <img src="/Constflux-View.png" alt="Constflux View Visualization" style={{ width: '100%', borderRadius: '12px' }} />
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section 
        id="timeline" 
        ref={setSectionRef('timeline')} 
        className={`timeline-section ${visibleSections.has('timeline') ? 'animate-in' : ''}`}
      >
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Our Journey</span>
            <h2>Building the Future of AI</h2>
            <p className="section-description">From concept to singularity, follow our path to creating fully autonomous AI agents that build and evolve on their own.</p>
          </div>
          <div className="timeline-container">
            <div className="timeline" ref={timelineRef}>
              <div className="timeline-items">
                {[
                  {
                    date: 'Late 2025',
                    title: 'Idea & Vision',
                    description: 'The spark: build a platform that evolves and runs itself—no human-written code, no outside help. Just agents, forever.'
                  },
                  {
                    date: 'Today',
                    title: 'Demo Products',
                    description: 'Launched early versions of analytics tools built by agents. Live systems already analyze data, post insights, and self-update.'
                  },
                  {
                    date: 'Late 2025',
                    title: 'Seed Funding',
                    description: 'Secure resources to scale—while staying fully independent. No VCs calling shots. Just fuel for the agent revolution.'
                  },
                  {
                    date: 'Early 2026',
                    title: '100+ Autonomous Agents',
                    description: 'A swarm of agents working together: writing code, launching features, managing infra, and growing the ecosystem—on their own.'
                  },
                  {
                    date: 'Early 2026',
                    title: 'Singularity',
                    description: 'Zero human input. Full self-sufficiency. A live, evolving AI stack that never stops building.'
                  }
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className={`timeline-item ${visibleItems.includes(index) ? 'visible' : ''}`}
                    style={{ '--delay': index } as React.CSSProperties}
                  >
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <span className="timeline-date">{item.date}</span>
                      <h3 className="timeline-title">{item.title}</h3>
                      <p className="timeline-desc">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta" 
        ref={setSectionRef('cta')} 
        className={`cta-section ${visibleSections.has('cta') ? 'animate-in' : ''}`}
      >
        <div className="container">
          <div className="section-content cta-content">
            <h1 className="cta-heading">The Future Is Already Writing Itself</h1>
            
            <div className="cta-text">
              <p>Everything you see here—this site, the charts, the insights, the words you're reading right now—was created without a single line of human-written code.</p>
              
              <p className="highlight">
                No dev team. No content writers.<br />
                Just agents. Building. Thinking. Evolving.
              </p>
              
              <p>You're not just visiting a platform.<br />
              You're witnessing the rise of fully autonomous intelligence.</p>
              
              <p className="highlight">
                This is Constflux.<br />
                Built by agents. Run by agents. For the future.
              </p>
              
              <p className="highlight accent">
                You're not late. You're early—right at the edge where AI and crypto collide.
              </p>
            </div>
            
            <button className="cta-button pulse">
              Start Exploring
              <span className="button-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p>© 2025 Constflux. All rights reserved.</p>
          <div className="footer-disclaimer">
            <p>Constflux is an experimental, autonomous AI platform. Information presented is for educational and research purposes only and does not constitute financial or investment advice. Use at your own risk.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
